import React, { useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { UncontrolledAccordion, AccordionItem, Row, Col, Button } from 'reactstrap';
import paginationFactory, { PaginationProvider } from 'react-bootstrap-table2-paginator';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import BootstrapTable from 'react-bootstrap-table-next';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import 'boxicons';
import styled from 'styled-components';

import { useAuth } from 'hooks/useAuth';
import { getFirebaseBackend } from '../../../../helpers/firebaseHelper';
import { AccordionTitle, AccordionTable, ActionWrapper, AccordionNameRow } from './styles';

import {
  statusColorClass,
  statusNumber,
  priorityColorClass,
  priorityLetter,
  priorityNumber,
} from '../../../../constants';
import TaskContainerDatePicker from 'components/Task-Container-Date-Picker';
import UserAvatar from 'components/UserAvatar';
import { getTaskColumns } from 'pages/tasks/config/taskTableColumns';
import { getDefaultSorted, getPageOptions } from 'pages/tasks/config/tableConfig';
import AvatarPlaceholder from 'assets/images/Avatar.svg';

const AccordionList = ({ column, data }) => {
  const firebaseHelper = getFirebaseBackend();
  const { t: translate } = useTranslation();
  const { user } = useAuth();
  const { adminStatus } = user;
  const accountId = user?.account;
  const [tasks, setTasks] = useState([]);

  const statusElem = (meetingStatus) => (
    <span className={`status ${statusColorClass[meetingStatus]} badge rounded-pill font-size-12`}>
      {translate(statusNumber[meetingStatus])}
    </span>
  );

  const complexityElem = (meetingComplexity) => (
    <div
      className={`${priorityColorClass[meetingComplexity]} badge rounded-pill font-size-14 px-3 py-1`}
    >
      {translate(priorityLetter[meetingComplexity])}
    </div>
  );

  const impactElem = (meetingImpact) => (
    <div
      className={`${priorityColorClass[meetingImpact]} badge rounded-pill font-size-14 px-3 py-1`}
    >
      {translate(priorityLetter[meetingImpact])}
    </div>
  );

  const dateDeliveryElem = (meetingDateDelivery, meetingId) => (
    <TaskContainerDatePicker date={meetingDateDelivery} id={meetingId} isDateDelivery anyDate />
  );

  const dateImplementationElem = (meetingDateImplementation, meetingId) => (
    <TaskContainerDatePicker
      date={meetingDateImplementation}
      id={meetingId}
      isDateImplementation
      anyDate
    />
  );

  const handleTaskRemove = (id) => {
    firebaseHelper.removeTask(accountId, id);
  };

  const actionButtons = (id) => {
    const actionElem =
      adminStatus !== 'Client' ? (
        <ActionWrapper>
          <Link to={`/edit-task?id=${id}&acc=${accountId}`}>
            <ButtonEditStyled>
              <box-icon name="pencil" color="#FFF" size="1.2rem" />
            </ButtonEditStyled>
          </Link>

          <ButtonDeleteStyled onClick={() => handleTaskRemove(id)}>
            <box-icon name="trash" color="#FFF" size="1.2rem" />
          </ButtonDeleteStyled>
        </ActionWrapper>
      ) : (
        <ActionWrapper>
          <Link to={`/tasks?id=${id}&acc=${accountId}`}>
            <ButtonViewStyled>
              <box-icon name="show" color="#FFF" size="1.2rem" />
            </ButtonViewStyled>
          </Link>
        </ActionWrapper>
      );

    return actionElem;
  };

  const avatarElem = (assignedTo) => {
    return (
      <div className="avatar-group justify-content-center">
        {_.map(assignedTo, (member, index) => {
          if (index > 2) return;

          return (
            <div className="avatar-group-item" key={index}>
              <UserAvatar userId={member} />
            </div>
          );
        })}
        {_.size(assignedTo) > 2 && (
          <div className="avatar-group-item">
            <div className="avatar-xs">
              <span
                className="avatar-title rounded-circle text-white font-size-16"
                style={{ backgroundColor: 'black' }}
              >
                {`${_.size(assignedTo) - 2}+`}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const tasksData = data ?? [];

    const newData = tasksData.map((task) => {
      const assignedTo = task.assignedTo || [];

      return {
        position: task.position,
        name: <AccordionNameRow>{task.name}</AccordionNameRow>,
        nameValue: task.name,
        status: statusElem(task.status),
        statusValue: task.status,
        assignedTo: task.assignedTo ? (
          avatarElem(assignedTo)
        ) : (
          <img src={AvatarPlaceholder} alt="Avatar Placeholder" />
        ),
        assignedToValue: assignedTo.length,
        complexity: task.complexity ? complexityElem(task.complexity) : '-',
        complexityValue: task.complexity ? priorityNumber[task.complexity] : 0,
        impact: task.impact ? impactElem(task.impact) : '-',
        impactValue: task.impact ? priorityNumber[task.impact] : 0,
        delivery: dateDeliveryElem(task.dateDelivery, task.id),
        deliveryValue: task.dateDelivery ?? 0,
        implementation: dateImplementationElem(task.dateImplementation, task.id),
        implementationValue: task.dateImplementation ?? 0,
        action: actionButtons(task.id),
      };
    });
    setTasks(newData);
  }, [data]);

  return (
    <UncontrolledAccordionStyled
      className="mb-4 overflow-visible"
      defaultOpen={['1', '2', '3', '4', '5']}
      stayOpen
    >
      <AccordionItem>
        <AccordionTitle targetId="1" className="p-0 border-0">
          {`${translate(column)} (${data?.length ?? 0})`}
        </AccordionTitle>

        <AccordionTable accordionId="1">
          <Row>
            <Col lg="12" className="overflow-visible">
              <PaginationProvider
                pagination={paginationFactory(getPageOptions(tasks.length))}
                keyField="id"
                columns={getTaskColumns(translate)}
                data={tasks}
              >
                {({ paginationTableProps }) => (
                  <ToolkitProvider keyField="id" columns={getTaskColumns(translate)} data={tasks}>
                    {(toolkitProps) => (
                      <React.Fragment>
                        <Row>
                          <Col xl="12">
                            <div className="table-responsive overflow-visible">
                              <BootstrapTable
                                keyField="id"
                                responsive
                                hover
                                bordered={false}
                                striped={false}
                                defaultSorted={getDefaultSorted}
                                classes={'table align-middle table-nowrap'}
                                headerWrapperClasses={'thead-light'}
                                {...toolkitProps.baseProps}
                                {...paginationTableProps}
                              />
                            </div>
                          </Col>
                        </Row>
                      </React.Fragment>
                    )}
                  </ToolkitProvider>
                )}
              </PaginationProvider>
            </Col>
          </Row>
        </AccordionTable>
      </AccordionItem>
    </UncontrolledAccordionStyled>
  );
};

const UncontrolledAccordionStyled = styled(UncontrolledAccordion)`
  .accordion-button {
    box-shadow: none !important;
  }
`;

const ButtonEditStyled = styled(Button)`
  background-color: #50a5f1;
  border: none;

  :hover {
    background-color: #4098e0;
  }
`;

const ButtonDeleteStyled = styled(Button)`
  background-color: #f46a6a;
  border: none;

  :hover {
    background-color: #e04f4f;
  }
`;

const ButtonViewStyled = styled(Button)`
  background-color: #50a5f1;
  border: none;

  :hover {
    background-color: #4098e0;
  }
`;

AccordionList.displayName = 'Accordion List Component';

AccordionList.propTypes = {
  column: PropTypes.string,
  data: PropTypes.array,
};

const arePropsEqual = (prevProps, nextProps) => {
  const isColumnEquals = prevProps.column === nextProps.column;
  const isDataEquals = JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data);

  return isColumnEquals && isDataEquals;
};

export default memo(AccordionList, arePropsEqual);
